import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertCircle,
  CheckCircle,
  Eye,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Send,
  Trash2,
  X,
} from "lucide-react";
import {
  NEWSLETTER_STATUS_CLASS,
  NEWSLETTER_STATUS_LABELS,
  CampaignSummary,
  Newsletter,
  NewsletterBlock,
  NewsletterRecipient,
  NewsletterStats,
  NewsletterTemplate,
  withBlockIds,
} from "../../../model/NewsletterCampaign";
import { NewsletterGroup } from "../../../model/Newsletter";
import {
  cancelNewsletter,
  createNewsletter,
  deleteNewsletter,
  getNewsletterStats,
  listNewsletterGroups,
  listNewsletterRecipients,
  listNewsletterTemplates,
  listNewsletters,
  listNiposomCampaigns,
  newsletterErrorMessage,
  previewNewsletter,
  retryFailedNewsletter,
  scheduleNewsletter,
  sendNewsletterNow,
  updateNewsletter,
} from "../../../helpers/newsletterCampaignsApi";
import NewsletterBlockEditor from "./NewsletterBlockEditor";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500";
const buttonPrimary =
  "inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50";
const buttonSecondary =
  "inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50";

const MERGE_TAGS = [
  "{{ first_name }}",
  "{{ name }}",
  "{{ email }}",
  "{{ store_name }}",
  "{{ unsubscribe_url }}",
  "{{ campaign.name }}",
];

const NewsletterCampaigns: React.FC = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [templates, setTemplates] = useState<NewsletterTemplate[]>([]);
  const [groups, setGroups] = useState<NewsletterGroup[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editing, setEditing] = useState<Newsletter | null>(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [blocks, setBlocks] = useState<NewsletterBlock[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [scheduledAt, setScheduledAt] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [stats, setStats] = useState<NewsletterStats | null>(null);
  const [failedRecipients, setFailedRecipients] = useState<
    NewsletterRecipient[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [copiedTag, setCopiedTag] = useState<string | null>(null);
  const busyRef = useRef(false);
  const loadSeq = useRef(0);
  const campaignsSeq = useRef(0);

  const loadCampaigns = useCallback(async () => {
    const seq = ++campaignsSeq.current;
    try {
      const list = await listNiposomCampaigns();
      if (seq !== campaignsSeq.current) return;
      setCampaigns(list);
      setCampaignsError(null);
    } catch {
      if (seq !== campaignsSeq.current) return;
      setCampaigns([]);
      setCampaignsError(
        "Campanhas indisponíveis — verifica a ligação ao catálogo Niposom.",
      );
    }
  }, []);

  const refreshNewsletters = useCallback(async () => {
    try {
      const list = await listNewsletters({ page: 1, perPage: 50 });
      setNewsletters(list.data);
    } catch {
      // Keep the stale list; the next explicit load surfaces the error.
    }
  }, []);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const [list, tpls, grps] = await Promise.all([
        listNewsletters({ page: 1, perPage: 50 }),
        listNewsletterTemplates(),
        listNewsletterGroups(),
      ]);
      if (seq !== loadSeq.current) return;
      setNewsletters(list.data);
      setTemplates(tpls);
      setGroups(grps);
      void loadCampaigns();
    } catch (e: any) {
      if (seq !== loadSeq.current) return;
      setError(newsletterErrorMessage(e));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [loadCampaigns]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll only the campaign list while something is sending, without flipping
  // the table to a spinner or refetching templates/groups/campaigns.
  const anySending = newsletters.some((n) => n.status === "sending");
  useEffect(() => {
    if (!anySending) return;
    const timer = setInterval(() => {
      void refreshNewsletters();
    }, 5000);
    return () => clearInterval(timer);
  }, [anySending, refreshNewsletters]);

  const copyMergeTag = async (tag: string) => {
    if (!navigator.clipboard) {
      setCopiedTag(null);
      return;
    }
    try {
      await navigator.clipboard?.writeText(tag);
      setCopiedTag(tag);
      window.setTimeout(() => setCopiedTag(null), 1500);
    } catch {
      setCopiedTag(null);
    }
  };

  const resetEditor = () => {
    setEditing(null);
    setName("");
    setSubject("");
    setTemplateId("");
    setBlocks([]);
    setGroupIds([]);
    setTrackingEnabled(true);
    setScheduledAt("");
    setPreview(null);
  };

  const closeEditor = () => {
    const dirty = Boolean(
      name || subject || blocks.length > 0 || groupIds.length > 0,
    );
    if (dirty && !confirm("Descartar as alterações não guardadas?")) return;
    resetEditor();
  };

  const openNew = () => {
    resetEditor();
    setEditing({
      id: "",
      tenant_id: "",
      name: "",
      subject: "",
      blocks: [],
      status: "draft",
      group_ids: [],
      tracking_enabled: true,
      created_at: "",
      updated_at: "",
    });
  };

  const openEdit = (item: Newsletter) => {
    setEditing(item);
    setName(item.name);
    setSubject(item.subject);
    setTemplateId(item.template_id || "");
    setBlocks(withBlockIds(item.blocks || []));
    setGroupIds(item.group_ids || []);
    setTrackingEnabled(item.tracking_enabled);
    setScheduledAt(toLocalDateTimeInput(item.scheduled_at));
    setPreview(null);
  };

  const applyTemplate = (id: string) => {
    const template = templates.find((t) => t.id === id);
    if (
      template &&
      blocks.length > 0 &&
      !confirm("Substituir os blocos atuais pelo template?")
    ) {
      return;
    }
    setTemplateId(id);
    if (template) {
      setBlocks(
        withBlockIds(JSON.parse(JSON.stringify(template.blocks || []))),
      );
    }
  };

  const run = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await action();
    } catch (e: any) {
      setError(newsletterErrorMessage(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const toLocalDateTimeInput = (value?: string | null): string => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const save = async (): Promise<Newsletter | null> => {
    if (!editing) return null;
    setError(null);
    const payload = {
      name,
      subject,
      template_id: templateId || null,
      blocks,
      group_ids: groupIds,
      tracking_enabled: trackingEnabled,
    };
    try {
      const saved = editing.id
        ? await updateNewsletter(editing.id, payload)
        : await createNewsletter(payload);
      setEditing(saved);
      setSuccess("Newsletter guardada.");
      await load();
      return saved;
    } catch (e: any) {
      setError(newsletterErrorMessage(e));
      return null;
    }
  };

  const audienceSummary = () => {
    const names = groups
      .filter((g) => groupIds.includes(g.id))
      .map((g) => g.name)
      .join(", ");
    return `${groupIds.length} lista(s): ${names}`;
  };

  const handlePreview = () =>
    run(async () => {
      const saved = await save();
      if (!saved) return;
      setPreview(await previewNewsletter(saved.id));
    });

  const handleSend = () =>
    run(async () => {
      if (groupIds.length === 0) {
        setError("Seleciona pelo menos uma lista de destinatários.");
        return;
      }
      if (
        !confirm(
          `Enviar agora para ${audienceSummary()}?\n\nEsta ação não pode ser anulada.`,
        )
      ) {
        return;
      }
      const saved = await save();
      if (!saved) return;
      await sendNewsletterNow(saved.id);
      setSuccess("Envio iniciado.");
      await load();
      resetEditor();
    });

  const handleSchedule = () =>
    run(async () => {
      if (groupIds.length === 0) {
        setError("Seleciona pelo menos uma lista de destinatários.");
        return;
      }
      if (!scheduledAt) {
        setError("Escolhe uma data e hora.");
        return;
      }
      if (
        !confirm(
          `Agendar para ${new Date(scheduledAt).toLocaleString("pt-PT")} — ${audienceSummary()}?`,
        )
      ) {
        return;
      }
      const saved = await save();
      if (!saved) return;
      await scheduleNewsletter(saved.id, new Date(scheduledAt).toISOString());
      setSuccess("Newsletter agendada.");
      await load();
      resetEditor();
    });

  const handleCancel = (item: Newsletter) =>
    run(async () => {
      if (!confirm("Cancelar esta newsletter?")) return;
      await cancelNewsletter(item.id);
      setSuccess("Newsletter cancelada.");
      await load();
    });

  const handleDelete = (item: Newsletter) =>
    run(async () => {
      if (!confirm(`Eliminar a newsletter "${item.name}"?`)) return;
      await deleteNewsletter(item.id);
      setSuccess("Newsletter eliminada.");
      await load();
    });

  const handleRetryFailed = (item: Newsletter) =>
    run(async () => {
      if (
        !confirm(`Reenviar apenas os destinatários falhados de "${item.name}"?`)
      ) {
        return;
      }
      await retryFailedNewsletter(item.id);
      setSuccess("Reenvio dos destinatários falhados iniciado.");
      await load();
    });

  const handleStats = (item: Newsletter) =>
    run(async () => {
      const [st, recs] = await Promise.all([
        getNewsletterStats(item.id),
        listNewsletterRecipients(item.id),
      ]);
      setStats(st);
      setFailedRecipients(recs.data.filter((r) => r.status === "failed"));
    });

  const groupOptions = useMemo(
    () => groups.map((g) => ({ id: g.id, name: g.name })),
    [groups],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Newsletters</h2>
          <p className="text-sm text-gray-500">
            {newsletters.length} campanha(s) neste tenant.
          </p>
        </div>
        <button
          type="button"
          className={buttonPrimary}
          onClick={openNew}
          disabled={busy}
        >
          <Plus size={16} /> Nova newsletter
        </button>
      </div>

      <div aria-live="polite">
        {success && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
            <CheckCircle size={16} /> {success}
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <AlertCircle size={16} /> {error}
          </div>
        )}
        {campaignsError && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            <AlertCircle size={16} /> {campaignsError}
            <button
              type="button"
              className="ml-auto rounded border border-amber-300 px-2 py-1 text-xs"
              onClick={() => loadCampaigns()}
            >
              Tentar novamente
            </button>
          </div>
        )}
      </div>

      {!editing && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Nome</th>
                <th className="px-3 py-2">Assunto</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Agendada</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-gray-500"
                  >
                    <Loader2 className="mx-auto animate-spin" size={20} />
                  </td>
                </tr>
              ) : newsletters.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-gray-500"
                  >
                    Ainda não há newsletters.
                  </td>
                </tr>
              ) : (
                newsletters.map((item) => (
                  <tr key={item.id}>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {item.name}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{item.subject}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${NEWSLETTER_STATUS_CLASS[item.status]}`}
                      >
                        {NEWSLETTER_STATUS_LABELS[item.status]}
                      </span>
                      {item.failure_reason && (
                        <p className="mt-1 text-xs text-red-700">
                          {item.failure_reason}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {item.scheduled_at
                        ? new Date(item.scheduled_at).toLocaleString("pt-PT")
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {(item.status === "draft" ||
                          item.status === "cancelled") && (
                          <button
                            type="button"
                            className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                            onClick={() => openEdit(item)}
                            title="Editar"
                            aria-label={`Editar ${item.name}`}
                            disabled={busy}
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                          onClick={() => handleStats(item)}
                          title="Estatísticas"
                          aria-label={`Estatísticas de ${item.name}`}
                          disabled={busy}
                        >
                          <Eye size={16} />
                        </button>
                        {(item.status === "scheduled" ||
                          item.status === "sending") && (
                          <button
                            type="button"
                            className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                            onClick={() => handleCancel(item)}
                            title="Cancelar"
                            aria-label={`Cancelar ${item.name}`}
                            disabled={busy}
                          >
                            <X size={16} />
                          </button>
                        )}
                        {(item.status === "partial" ||
                          item.status === "failed") && (
                          <button
                            type="button"
                            className="rounded p-1.5 text-amber-700 hover:bg-gray-100"
                            onClick={() => handleRetryFailed(item)}
                            title="Reenviar falhados"
                            aria-label={`Reenviar falhados de ${item.name}`}
                            disabled={busy}
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                        {(item.status === "draft" ||
                          item.status === "cancelled") && (
                          <button
                            type="button"
                            className="rounded p-1.5 text-red-600 hover:bg-gray-100"
                            onClick={() => handleDelete(item)}
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="space-y-4 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-md font-semibold text-gray-800">
              {editing.id ? "Editar newsletter" : "Nova newsletter"}
            </h3>
            <button
              type="button"
              className={buttonSecondary}
              onClick={closeEditor}
            >
              <X size={16} /> Fechar
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <input
              className={inputClass}
              placeholder="Nome interno"
              aria-label="Nome interno"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setPreview(null);
              }}
            />
            <input
              className={inputClass}
              placeholder="Assunto do email"
              aria-label="Assunto do email"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setPreview(null);
              }}
            />
            <select
              className={inputClass}
              aria-label="Template"
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
            >
              <option value="">Sem template</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.is_system ? " (base)" : ""}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={trackingEnabled}
                onChange={(e) => setTrackingEnabled(e.target.checked)}
              />
              Registar aberturas e cliques
            </label>
          </div>

          <div>
            <p className="mb-1 text-sm font-medium text-gray-700">
              Listas de destinatários
            </p>
            <div className="flex flex-wrap gap-3">
              {groupOptions.length === 0 && (
                <span className="text-sm text-gray-500">
                  Cria grupos no separador Subscritores.
                </span>
              )}
              {groupOptions.map((group) => (
                <label
                  key={group.id}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={groupIds.includes(group.id)}
                    onChange={(e) =>
                      setGroupIds((current) =>
                        e.target.checked
                          ? [...current, group.id]
                          : current.filter((id) => id !== group.id),
                      )
                    }
                  />
                  {group.name}
                </label>
              ))}
            </div>
          </div>
          {groupIds.length === 0 && (
            <p className="mt-1 text-sm text-amber-700">
              Seleciona pelo menos uma lista. Sem lista não é possível enviar.
            </p>
          )}

          <NewsletterBlockEditor
            blocks={blocks}
            onChange={(next) => {
              setBlocks(next);
              setPreview(null);
            }}
            campaigns={campaigns}
          />

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="mb-2 text-xs font-medium text-gray-700">
              Variáveis (clica para copiar e cola no texto):
            </p>
            <div className="flex flex-wrap gap-2">
              {MERGE_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => copyMergeTag(tag)}
                  className="rounded border border-gray-300 bg-white px-2 py-1 font-mono text-xs text-gray-700 hover:bg-gray-100"
                >
                  {tag}
                </button>
              ))}
            </div>
            {copiedTag && (
              <p className="mt-1 text-xs text-green-700" role="status">
                Copiado: {copiedTag}
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Pré-visualização usa Maria Silva (maria@example.com).
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <button
              type="button"
              className={buttonPrimary}
              onClick={() =>
                run(async () => {
                  await save();
                })
              }
              disabled={busy}
            >
              Guardar
            </button>
            <button
              type="button"
              className={buttonSecondary}
              onClick={handlePreview}
              disabled={busy}
            >
              <Eye size={16} /> Pré-visualizar
            </button>
            <button
              type="button"
              className={buttonSecondary}
              onClick={handleSend}
              disabled={busy}
            >
              <Send size={16} /> Enviar agora
            </button>
            <div className="flex items-end gap-2">
              <input
                className={inputClass}
                type="datetime-local"
                aria-label="Data e hora de agendamento"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
              <button
                type="button"
                className={buttonSecondary}
                onClick={handleSchedule}
                disabled={busy}
              >
                Agendar
              </button>
            </div>
          </div>

          {preview && (
            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">
                Pré-visualização
              </p>
              <iframe
                title="Pré-visualização da newsletter"
                srcDoc={preview}
                sandbox=""
                className="h-96 w-full rounded-lg border border-gray-200"
              />
            </div>
          )}
        </div>
      )}

      {stats && (
        <div className="rounded-lg border border-gray-200 p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-md font-semibold text-gray-800">
              Estatísticas — {NEWSLETTER_STATUS_LABELS[stats.status]}
            </h3>
            <button
              type="button"
              className={buttonSecondary}
              onClick={() => {
                setStats(null);
                setFailedRecipients([]);
              }}
            >
              <X size={16} /> Fechar
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div>Total: {stats.total}</div>
            <div>Enviados: {stats.sent}</div>
            <div>Falhados: {stats.failed}</div>
            <div>Pendentes: {stats.pending}</div>
            <div>Aberturas únicas: {stats.unique_opens}</div>
            <div>Cliques únicos: {stats.unique_clicks}</div>
          </div>
          {failedRecipients.length > 0 && (
            <div className="mt-3">
              <p className="mb-1 text-sm font-medium text-gray-700">
                Destinatários falhados ({failedRecipients.length})
              </p>
              <ul className="max-h-48 space-y-1 overflow-auto text-xs text-red-700">
                {failedRecipients.map((recipient) => (
                  <li key={recipient.id}>
                    {recipient.email}
                    {recipient.error ? ` — ${recipient.error}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsletterCampaigns;
